import { roleAlignmentScore } from '../../recommendations/engine/scoring/dimensionScores';
import type { HiddenOpportunity } from '../components/HiddenOpportunities';

export interface HiddenOpportunitySource {
  id: string;
  title: string;
  company: string;
  location?: string;
  score: number;
}

export const HIDDEN_OPS_MIN_SCORE = 50;
export const HIDDEN_OPS_DISPLAY_LIMIT = 5;

const TITLE_STOP = new Set([
  'senior', 'junior', 'sr', 'jr', 'lead', 'staff', 'principal',
  'associate', 'the', 'and', 'for', 'iii', 'ii', 'i',
]);

function titleTokens(text: string): Set<string> {
  return new Set(
    text
      .toLowerCase()
      .replace(/[^a-z0-9+\s]/g, ' ')
      .split(/\s+/)
      .filter((w) => w.length > 2 && !TITLE_STOP.has(w)),
  );
}

/** True when the job title is not a same-role variant of the candidate title. */
export function titlesDiverge(fromTitle: string, jobTitle: string): boolean {
  const from = titleTokens(fromTitle);
  const to = titleTokens(jobTitle);
  if (from.size === 0 || to.size === 0) return false;

  let overlap = 0;
  for (const token of to) {
    if (from.has(token)) overlap += 1;
  }
  // Allow one shared generic token (e.g. "software") but not full role overlap
  return overlap < Math.min(from.size, to.size);
}

export function jobListToHiddenSources(
  jobs: Array<{
    id: string;
    title: string;
    company: string;
    location?: string | null;
    matchScore?: number;
    score?: number;
  }>,
): HiddenOpportunitySource[] {
  return jobs
    .map((job) => ({
      id: job.id,
      title: job.title,
      company: job.company,
      location: job.location ?? undefined,
      score: Math.round(job.matchScore ?? job.score ?? 0),
    }))
    .filter((j) => j.score >= HIDDEN_OPS_MIN_SCORE);
}

/**
 * High skill/semantic fit where the job title does not match the
 * candidate's current title — adjacent-role / pivot opportunities.
 */
export function buildHiddenOpportunities(
  currentTitle: string,
  jobs: HiddenOpportunitySource[],
  limit = HIDDEN_OPS_DISPLAY_LIMIT,
): HiddenOpportunity[] {
  const fromTitle = currentTitle.trim();
  if (!fromTitle) return [];

  return jobs
    .filter((job) => {
      if (job.score < HIDDEN_OPS_MIN_SCORE) return false;
      const alignment = roleAlignmentScore(fromTitle, undefined, job.title);
      return titlesDiverge(fromTitle, job.title) && alignment < 65;
    })
    .sort((a, b) => b.score - a.score)
    .slice(0, limit)
    .map((job) => ({
      id: job.id,
      fromTitle,
      toTitle: job.title,
      company: job.company,
      score: Math.round(job.score),
      location: job.location,
    }));
}

/** Illustrative examples for marketing / logged-out homepage. */
export const DEMO_HIDDEN_OPPORTUNITIES: HiddenOpportunity[] = [
  {
    id: 'demo-1',
    fromTitle: 'Software Engineer',
    toTitle: 'Solutions Architect',
    company: 'Enterprise SaaS',
    score: 87,
  },
  {
    id: 'demo-2',
    fromTitle: 'Senior Developer',
    toTitle: 'Engineering Manager',
    company: 'Growth-stage startup',
    score: 82,
  },
  {
    id: 'demo-3',
    fromTitle: 'Business Analyst',
    toTitle: 'Product Manager',
    company: 'Fintech',
    score: 79,
  },
];
